import React, { useState, useEffect, useRef } from 'react';
import './interactive.css';

const PIPELINE_STAGES = [
  { id: 'facts', name: '1. Fact Gathering', icon: 'fas fa-search', desc: 'SSH Handshake & OS Detectie' },
  { id: 'vsphere', name: '2. vSphere Provisioning', icon: 'fas fa-cloud-upload-alt', desc: 'VMware vCenter VM Deployment' },
  { id: 'caddy', name: '3. Caddy Reverse Proxy', icon: 'fas fa-lock', desc: 'Automated Let\'s Encrypt SSL' },
  { id: 'haproxy', name: '4. HAProxy Pool', icon: 'fas fa-network-wired', desc: 'High-Availability Load Balancer' },
  { id: 'security', name: '5. CIS Hardening', icon: 'fas fa-shield-alt', desc: 'SELinux & Firewalld Rules' },
  { id: 'recap', name: '6. Verified Healthy', icon: 'fas fa-check-double', desc: 'Zero-Downtime Cluster Klaar' }
];

const PLAYBOOK_LINES = [
  { stage: 'facts', type: 'play', text: 'PLAY [Deploy Enterprise Web Infrastructure & SSL Proxy] **********************' },
  { stage: 'facts', type: 'task', text: 'TASK [Gathering Facts] **********************************************************' },
  { stage: 'facts', type: 'ok', text: 'ok: [proxy-node-01] => (SUCCESS: Python 3.11 detected, OS: Red Hat Enterprise Linux 9)' },
  { stage: 'facts', type: 'ok', text: 'ok: [app-node-01]   => (SUCCESS: Connected via SSH key, Fact cache updated)' },
  { stage: 'vsphere', type: 'task', text: 'TASK [vcenter-guest : Provision Virtual Machines via VMware vSphere API] **********' },
  { stage: 'vsphere', type: 'changed', text: 'changed: [app-node-01] => {"changed": true, "ip": "10.0.10.24", "powerState": "poweredOn"}' },
  { stage: 'vsphere', type: 'changed', text: 'changed: [app-node-02] => {"changed": true, "ip": "10.0.10.25", "powerState": "poweredOn"}' },
  { stage: 'caddy', type: 'task', text: 'TASK [caddy : Deploy Reverse Proxy & Automated Let\'s Encrypt SSL] ***************' },
  { stage: 'caddy', type: 'changed', text: 'changed: [proxy-node-01] => {"changed": true, "config": "/etc/caddy/Caddyfile", "ssl": "active"}' },
  { stage: 'haproxy', type: 'task', text: 'TASK [haproxy : Configure High-Availability Load Balancing Pool] ******************' },
  { stage: 'haproxy', type: 'changed', text: 'changed: [proxy-node-01] => {"backends": 2, "algorithm": "roundrobin", "status": "reloaded"}' },
  { stage: 'security', type: 'task', text: 'TASK [security : Enforce CIS Linux Hardening & Firewall Rulesets] **************' },
  { stage: 'security', type: 'ok', text: 'ok: [proxy-node-01] => {"firewall": "ufw/iptables active", "sshRootLogin": "disabled"}' },
  { stage: 'security', type: 'ok', text: 'ok: [app-node-01]   => {"selinux": "enforcing", "openPorts": [443, 80]}' },
  { stage: 'security', type: 'ok', text: 'ok: [app-node-02]   => {"selinux": "enforcing", "openPorts": [443, 80]}' },
  { stage: 'recap', type: 'recap', text: 'PLAY RECAP *********************************************************************' },
  { stage: 'recap', type: 'ok', text: 'proxy-node-01  : ok=4    changed=2    unreachable=0    failed=0' },
  { stage: 'recap', type: 'ok', text: 'app-node-01    : ok=3    changed=1    unreachable=0    failed=0' },
  { stage: 'recap', type: 'ok', text: 'app-node-02    : ok=3    changed=1    unreachable=0    failed=0' },
  { stage: 'recap', type: 'play', text: '✨ Playbook run completed successfully in 4.82s! Zero downtime.' }
];

const NODE_DETAILS = {
  controller: {
    name: 'Ansible Control Node',
    role: 'Orchestrator & CI/CD Runner',
    ip: '10.0.10.5',
    os: 'Red Hat Enterprise Linux 9 (Python 3.11)',
    details: 'Voert Ansible playbooks uit via idempotente SSH-verbindingen. Beheert vSphere API keys en inventaris.'
  },
  vsphere: {
    name: 'VMware vSphere API Cloud',
    role: 'Hypervisor & Compute Cluster',
    ip: '10.0.10.1 (vCenter)',
    os: 'VMware ESXi 8.0 Cluster',
    details: 'Provisioneert automatisch nieuwe VM templates vanuit golden images op basis van community.vmware playbooks.'
  },
  proxy: {
    name: 'proxy-node-01 (HAProxy + Caddy)',
    role: 'SSL Termination & Load Balancer',
    ip: '10.0.10.20',
    os: 'RHEL 9 • Caddy v2 + HAProxy 2.8',
    details: 'Ontvangt inkomend HTTPS-verkeer (Poort 443) met automatische Let\'s Encrypt certificaten en verdeelt load via Round Robin.'
  },
  app1: {
    name: 'app-node-01',
    role: 'Web Application Server #1',
    ip: '10.0.10.24',
    os: 'RHEL 9 • SELinux Enforcing',
    details: 'Gehard met CIS benchmarks, firewalld geconfigureerd, SSH root login uitgeschakeld en actieve healthchecks.'
  },
  app2: {
    name: 'app-node-02',
    role: 'Web Application Server #2',
    ip: '10.0.10.25',
    os: 'RHEL 9 • SELinux Enforcing',
    details: 'Redundante worker node. Schakelt naadloos in bij piekdrukte of uitval van app-node-01.'
  }
};

const AnsibleSimulator = () => {
  const [lines, setLines] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentStage, setCurrentStage] = useState('idle');
  const [selectedNode, setSelectedNode] = useState('controller');
  const [viewMode, setViewMode] = useState('both'); // 'both', 'diagram', 'terminal'
  const terminalContainerRef = useRef(null);

  useEffect(() => {
    let timer;
    if (isRunning && currentIndex < PLAYBOOK_LINES.length) {
      timer = setTimeout(() => {
        const nextLine = PLAYBOOK_LINES[currentIndex];
        setLines(prev => [...prev, nextLine]);
        setCurrentStage(nextLine.stage);
        setCurrentIndex(prev => prev + 1);
      }, 340);
    } else if (currentIndex >= PLAYBOOK_LINES.length) {
      setIsRunning(false);
      setCurrentStage('recap');
    }
    return () => clearTimeout(timer);
  }, [isRunning, currentIndex]);

  // Keep internal terminal scrolled to bottom without moving the page window
  useEffect(() => {
    if (terminalContainerRef.current) {
      terminalContainerRef.current.scrollTop = terminalContainerRef.current.scrollHeight;
    }
  }, [lines]);

  const handleRun = () => {
    setLines([]);
    setCurrentIndex(0);
    setCurrentStage('facts');
    setIsRunning(true);
  };

  const handleReset = () => {
    setIsRunning(false);
    setLines([]);
    setCurrentIndex(0);
    setCurrentStage('idle');
  };

  // Determine node states based on playbook progression
  const isFactsDone = currentIndex >= 4;
  const isVsphereDone = currentIndex >= 7;
  const isCaddyDone = currentIndex >= 9;
  const isHaproxyDone = currentIndex >= 11;
  const isSecurityDone = currentIndex >= 15;
  const isAllDone = currentIndex >= PLAYBOOK_LINES.length;

  const getStageStatus = (stageId) => {
    const stageOrder = ['facts', 'vsphere', 'caddy', 'haproxy', 'security', 'recap'];
    const currentIdx = stageOrder.indexOf(currentStage);
    const thisIdx = stageOrder.indexOf(stageId);

    if (currentStage === 'idle') return 'pending';
    if (isAllDone) return 'done';
    if (currentStage === stageId) return 'active';
    if (currentIdx > thisIdx) return 'done';
    return 'pending';
  };

  return (
    <div className="interactive-box">
      {/* Header */}
      <div className="interactive-header">
        <div className="interactive-title">
          <i className="fas fa-sitemap" style={{ color: 'var(--first-color)' }}></i> Ansible Automation: Architectuur & Live Playbook
        </div>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button
            type="button"
            className={`ansible-btn ${viewMode === 'both' ? '' : 'ansible-btn--secondary'}`}
            style={{ padding: '0.25rem 0.65rem', fontSize: '0.72rem' }}
            onClick={() => setViewMode('both')}
          >
            Tekening + Terminal
          </button>
          <button
            type="button"
            className={`ansible-btn ${viewMode === 'diagram' ? '' : 'ansible-btn--secondary'}`}
            style={{ padding: '0.25rem 0.65rem', fontSize: '0.72rem' }}
            onClick={() => setViewMode('diagram')}
          >
            Alleen Tekening
          </button>
          <button
            type="button"
            className={`ansible-btn ${viewMode === 'terminal' ? '' : 'ansible-btn--secondary'}`}
            style={{ padding: '0.25rem 0.65rem', fontSize: '0.72rem' }}
            onClick={() => setViewMode('terminal')}
          >
            Alleen Terminal
          </button>
        </div>
      </div>

      <div className="interactive-content">
        {/* PIPELINE PROGRESS BAR */}
        <div className="ansible-pipeline-steps">
          {PIPELINE_STAGES.map((s) => {
            const status = getStageStatus(s.id);
            return (
              <div key={s.id} className={`ansible-step-pill ansible-step-pill--${status}`}>
                <div style={{ fontSize: '0.9rem' }}>
                  {status === 'active' ? (
                    <i className="fas fa-spinner fa-spin" style={{ color: '#f59e0b' }}></i>
                  ) : status === 'done' ? (
                    <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                  ) : (
                    <i className={s.icon}></i>
                  )}
                </div>
                <strong style={{ fontSize: '0.72rem' }}>{s.name}</strong>
                <span style={{ fontSize: '0.64rem', opacity: 0.75 }}>{s.desc}</span>
              </div>
            );
          })}
        </div>

        {/* VISUAL TOPOLOGY DRAWING */}
        {(viewMode === 'both' || viewMode === 'diagram') && (
          <div className="ansible-visual-canvas">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem' }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--title-color)' }}>
                <i className="fas fa-project-diagram" style={{ color: 'var(--first-color)', marginRight: '6px' }}></i>
                vSphere & Webstack Topologie
              </span>
              {isRunning && (
                <span style={{ fontSize: '0.72rem', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <i className="fas fa-sync fa-spin"></i> Playbook voert taken uit...
                </span>
              )}
            </div>

            <div className="ansible-topology-grid">
              {/* NODE 1: ANSIBLE CONTROLLER */}
              <div
                className={`ansible-node ${selectedNode === 'controller' ? 'ansible-node--active' : ''} ${isFactsDone ? 'ansible-node--done' : ''}`}
                onClick={() => setSelectedNode('controller')}
              >
                <div className="ansible-node-header">
                  <div className="ansible-node-icon" style={{ background: '#f97316', color: '#fff' }}>
                    <i className="fas fa-terminal"></i>
                  </div>
                  <span
                    className="ansible-node-badge"
                    style={isRunning ? { background: 'rgba(245, 158, 11, 0.2)', color: '#fbbf24' } : undefined}
                  >
                    {isRunning ? 'EXEC' : 'CONTROLLER'}
                  </span>
                </div>
                <div className="ansible-node-name">ansible-controller</div>
                <div className="ansible-node-meta">10.0.10.5 • RHEL 9</div>
                <div className={`ansible-node-subtext ${isFactsDone ? 'ansible-node-subtext--done' : ''}`}>
                  {isFactsDone ? '✓ SSH Keys & Facts OK' : '• Wacht op run'}
                </div>
              </div>

              {/* ARROW 1 */}
              <div className={`ansible-flow-arrow ${currentStage === 'facts' || currentStage === 'vsphere' ? 'ansible-flow-arrow--active' : ''}`}>
                <i className="fas fa-arrow-right"></i>
              </div>

              {/* NODE 2: VSPHERE / EDGE PROXY */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                {/* vSphere Cloud */}
                <div
                  className={`ansible-node ${selectedNode === 'vsphere' ? 'ansible-node--active' : ''} ${isVsphereDone ? 'ansible-node--done' : ''}`}
                  onClick={() => setSelectedNode('vsphere')}
                  style={{ padding: '0.65rem' }}
                >
                  <div className="ansible-node-header">
                    <div className="ansible-node-icon" style={{ background: '#0284c7', color: '#fff' }}>
                      <i className="fas fa-cloud"></i>
                    </div>
                    <span
                      className="ansible-node-badge"
                      style={isVsphereDone ? { background: 'rgba(16, 185, 129, 0.2)', color: '#10b981' } : undefined}
                    >
                      {isVsphereDone ? 'PROVISIONED' : 'VSPHERE API'}
                    </span>
                  </div>
                  <div className="ansible-node-name" style={{ fontSize: '0.78rem' }}>VMware vCenter</div>
                  <div className={`ansible-node-subtext ${isVsphereDone ? 'ansible-node-subtext--done' : ''}`}>
                    {isVsphereDone ? '✓ 2 VMs Uitgerold' : '• API Standby'}
                  </div>
                </div>

                {/* HAProxy & Caddy Node */}
                <div
                  className={`ansible-node ${selectedNode === 'proxy' ? 'ansible-node--active' : ''} ${isHaproxyDone && isCaddyDone ? 'ansible-node--done' : ''}`}
                  onClick={() => setSelectedNode('proxy')}
                  style={{ padding: '0.65rem' }}
                >
                  <div className="ansible-node-header">
                    <div className="ansible-node-icon" style={{ background: '#10b981', color: '#fff' }}>
                      <i className="fas fa-shield-alt"></i>
                    </div>
                    <span
                      className="ansible-node-badge"
                      style={isCaddyDone ? { background: 'rgba(16, 185, 129, 0.2)', color: '#10b981' } : undefined}
                    >
                      {isCaddyDone ? 'SSL & HAPROXY' : 'EDGE PROXY'}
                    </span>
                  </div>
                  <div className="ansible-node-name" style={{ fontSize: '0.78rem' }}>proxy-node-01</div>
                  <div className={`ansible-node-subtext ${isHaproxyDone ? 'ansible-node-subtext--done' : ''}`}>
                    {isHaproxyDone ? '✓ Port 443 SSL Active' : '• Wacht op Caddyfile'}
                  </div>
                </div>
              </div>

              {/* ARROW 2 */}
              <div className={`ansible-flow-arrow ${currentStage === 'caddy' || currentStage === 'haproxy' || currentStage === 'security' ? 'ansible-flow-arrow--active' : ''}`}>
                <i className="fas fa-arrow-right"></i>
              </div>

              {/* NODE 3: APPLICATION WORKERS CLUSTER */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                {/* App Worker 01 */}
                <div
                  className={`ansible-node ${selectedNode === 'app1' ? 'ansible-node--active' : ''} ${isSecurityDone ? 'ansible-node--done' : ''}`}
                  onClick={() => setSelectedNode('app1')}
                  style={{ padding: '0.65rem' }}
                >
                  <div className="ansible-node-header">
                    <div className="ansible-node-icon" style={{ background: '#8b5cf6', color: '#fff' }}>
                      <i className="fas fa-server"></i>
                    </div>
                    <span
                      className="ansible-node-badge"
                      style={isSecurityDone ? { background: 'rgba(16, 185, 129, 0.2)', color: '#10b981' } : undefined}
                    >
                      {isSecurityDone ? 'HARDENED' : 'NODE 01'}
                    </span>
                  </div>
                  <div className="ansible-node-name" style={{ fontSize: '0.78rem' }}>app-node-01 (10.0.10.24)</div>
                  <div className={`ansible-node-subtext ${isSecurityDone ? 'ansible-node-subtext--done' : ''}`}>
                    {isSecurityDone ? '✓ SELinux Enforcing • Port 80' : '• Offline'}
                  </div>
                </div>

                {/* App Worker 02 */}
                <div
                  className={`ansible-node ${selectedNode === 'app2' ? 'ansible-node--active' : ''} ${isSecurityDone ? 'ansible-node--done' : ''}`}
                  onClick={() => setSelectedNode('app2')}
                  style={{ padding: '0.65rem' }}
                >
                  <div className="ansible-node-header">
                    <div className="ansible-node-icon" style={{ background: '#8b5cf6', color: '#fff' }}>
                      <i className="fas fa-server"></i>
                    </div>
                    <span
                      className="ansible-node-badge"
                      style={isSecurityDone ? { background: 'rgba(16, 185, 129, 0.2)', color: '#10b981' } : undefined}
                    >
                      {isSecurityDone ? 'HARDENED' : 'NODE 02'}
                    </span>
                  </div>
                  <div className="ansible-node-name" style={{ fontSize: '0.78rem' }}>app-node-02 (10.0.10.25)</div>
                  <div className={`ansible-node-subtext ${isSecurityDone ? 'ansible-node-subtext--done' : ''}`}>
                    {isSecurityDone ? '✓ SELinux Enforcing • Port 80' : '• Offline'}
                  </div>
                </div>
              </div>
            </div>

            {/* NODE INSPECTOR DRAWER */}
            {selectedNode && NODE_DETAILS[selectedNode] && (
              <div className="ansible-node-inspector">
                <div className="ansible-inspector-title">
                  <span>
                    <i className="fas fa-info-circle"></i> Node Geïnspecteerd: {NODE_DETAILS[selectedNode].name}
                  </span>
                  <span className="ansible-inspector-ip">
                    {NODE_DETAILS[selectedNode].ip}
                  </span>
                </div>
                <div className="ansible-inspector-role">
                  Rol: {NODE_DETAILS[selectedNode].role} • {NODE_DETAILS[selectedNode].os}
                </div>
                <div className="ansible-inspector-details">
                  {NODE_DETAILS[selectedNode].details}
                </div>
              </div>
            )}
          </div>
        )}

        {/* TERMINAL OUTPUT */}
        {(viewMode === 'both' || viewMode === 'terminal') && (
          <div className="ansible-terminal-window">
            <div className="ansible-terminal-header">
              <span className="ansible-terminal-title">
                <i className="fas fa-terminal" style={{ color: 'var(--first-color)' }}></i>
                ansible-playbook deploy-webstack.yml
              </span>
              <span className="ansible-terminal-badge">RHEL 9 • SSH</span>
            </div>
            <div
              className="ansible-terminal"
              ref={terminalContainerRef}
              style={{ minHeight: viewMode === 'both' ? '200px' : '320px' }}
            >
              {lines.length === 0 ? (
                <div style={{ color: 'rgba(255,255,255,0.45)', fontStyle: 'italic', padding: '1rem 0' }}>
                  $ Druk op 'Start Playbook Run' hieronder om de geautomatiseerde deployment simulatie en visuele tekening te starten...
                </div>
              ) : (
                lines.map((l, idx) => (
                  <div key={idx} className={`ansible-line ansible-${l.type}`}>
                    {l.text}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* CONTROLS */}
        <div className="ansible-controls">
          <button
            type="button"
            className="ansible-btn"
            onClick={handleRun}
            disabled={isRunning}
            style={{ background: '#f97316' }}
          >
            <i className={isRunning ? "fas fa-spinner fa-spin" : "fas fa-play"}></i>
            {isRunning ? "Playbook wordt live uitgevoerd..." : (lines.length > 0 ? "Opnieuw Uitvoeren" : "Start Playbook Run")}
          </button>
          {lines.length > 0 && (
            <button type="button" className="ansible-btn ansible-btn--secondary" onClick={handleReset}>
              <i className="fas fa-redo"></i> Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnsibleSimulator;
